"use client";

import { AdjustmentsHorizontalIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useMemo, useState, Suspense } from "react";
import { useSearchParams } from "next/navigation";
import ProductCard from "@/components/molecules/product-card";
import ProductCardSkeletonLoader from "@/components/atoms/skeleton-loaders/product-card-skeleton-loader";
import PaginationControls from "@/components/molecules/pagination-controls";
import CTASection from "@/components/molecules/landing-section-two/discover";
import Footer from "@/components/molecules/landing-section-two/footer";
import { Button } from "@/components/ui/button";
import ProductFilters from "@/components/molecules/product-filters";
import { Sheet, SheetClose, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import ShopPageJumbotron from "@/components/molecules/jumbotron/jumbotron";
import { JumbotronItem } from "@/lib/types/shop";

function ShopContent() {
  const [isLoading, setIsLoading] = useState(false);
  const [checkedCategories, setCheckedCategories] = useState<{ [key: string]: boolean }>({});
  const [checkedBrands, setCheckedBrands] = useState<{ [key: string]: boolean }>({});
  const searchParams = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);
  // const router = useRouter();

  const pageFromQuery = Number(searchParams.get("page")) || 1;
  const meta = useMemo(() => {
    // Mocked meta; replace when connecting backend
    const totalItems = 100;
    const pageSize = 9;
    const totalPages = 10; // Math.max(1, Math.ceil(totalItems / pageSize))
    const currentPage = Math.max(1, Math.min(pageFromQuery, totalPages));
    return {
      totalItems,
      pageSize,
      totalPages,
      currentPage,
      hasNextPage: currentPage < totalPages,
      hasPreviousPage: currentPage > 1,
    };
  }, [pageFromQuery]);

  const categories = [
    {
      id: 1,
      name: "Furniture",
    },
    {
      id: 2,
      name: "Electronics",
    },
    {
      id: 3,
      name: "Clothing",
    },
    {
      id: 4,
      name: "Books",
    },
    {
      id: 5,
      name: "Beauty",
    },
    {
      id: 6,
      name: "Fashion",
    },
    {
      id: 7,
      name: "Sports",
    },
    {
      id: 8,
      name: "Toys",
    },
  ];

  const brands = [
    {
      id: 1,
      name: "Oraimo",
    },
    {
      id: 2,
      name: "Samsung",
    },
    {
      id: 3,
      name: "Apple",
    },
    {
      id: 4,
      name: "Nike",
    },
    {
      id: 5,
      name: "Adidas",
    },
    {
      id: 6,
      name: "Puma",
    },
    {
      id: 7,
      name: "Reebok",
    },
    {
      id: 8,
      name: "Under Armour",
    },
    {
      id: 9,
      name: "New Balance",
    },
    {
      id: 10,
      name: "Converse",
    },
    {
      id: 11,
      name: "Vans",
    },
    {
      id: 12,
      name: "Fila",
    },
    {
      id: 13,
      name: "Skechers",
    },
  ];

  const products = [
    {
      id: 1,
      title: "Product 1",
      price: 100000,
      image:
        "https://images.unsplash.com/photo-1633948393301-d43e3ec0e5cd?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      rating: 4.5,
      details:
        "Lorem ipsum dolor sit amet consectetur adipisicing elit. Dolores cumque, a voluptates porro sed atque voluptas voluptate repellendus ab veritatis illum, iste aut sit vero. Nesciunt tempore deserunt soluta eaque quibusdam, exercitationem ipsum nemo officiis minima molestiae cupiditate voluptatibus eius placeat sed repudiandae. Modi odit incidunt tenetur officia facere pariatur.",
      noOfReviews: 100,
      assetProtected: true,
    },
    {
      id: 2,
      title: "Product 2",
      price: 200000,
      image:
        "https://images.unsplash.com/photo-1633948393301-d43e3ec0e5cd?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      rating: 4.5,
      details:
        "Lorem ipsum dolor sit amet consectetur adipisicing elit. Dolores cumque, a voluptates porro sed atque voluptas voluptate repellendus ab veritatis illum, iste aut sit vero. Nesciunt tempore deserunt soluta eaque quibusdam, exercitationem ipsum nemo officiis minima molestiae cupiditate voluptatibus eius placeat sed repudiandae. Modi odit incidunt tenetur officia facere pariatur.",
      noOfReviews: 100,
      installationProtection: true,
    },
    {
      id: 3,
      title: "Product 3",
      price: 300000,
      image:
        "https://images.unsplash.com/photo-1633948393301-d43e3ec0e5cd?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      rating: 4.5,
      details:
        "Lorem ipsum dolor sit amet consectetur adipisicing elit. Dolores cumque, a voluptates porro sed atque voluptas voluptate repellendus ab veritatis illum, iste aut sit vero. Nesciunt tempore deserunt soluta eaque quibusdam, exercitationem ipsum nemo officiis minima molestiae cupiditate voluptatibus eius placeat sed repudiandae. Modi odit incidunt tenetur officia facere pariatur.",
      noOfReviews: 100,
    },
    {
      id: 4,
      title: "Product 4",
      price: 400000,
      image:
        "https://images.unsplash.com/photo-1633948393301-d43e3ec0e5cd?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      rating: 4.5,
      details:
        "Lorem ipsum dolor sit amet consectetur adipisicing elit. Dolores cumque, a voluptates porro sed atque voluptas voluptate repellendus ab veritatis illum, iste aut sit vero. Nesciunt tempore deserunt soluta eaque quibusdam, exercitationem ipsum nemo officiis minima molestiae cupiditate voluptatibus eius placeat sed repudiandae. Modi odit incidunt tenetur officia facere pariatur.",
      noOfReviews: 100,
      assetProtected: true,
      installationProtection: true,
    },
    {
      id: 5,
      title: "Product 5",
      price: 500000,
      image:
        "https://images.unsplash.com/photo-1633948393301-d43e3ec0e5cd?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      rating: 4.5,
      details:
        "Lorem ipsum dolor sit amet consectetur adipisicing elit. Dolores cumque, a voluptates porro sed atque voluptas voluptate repellendus ab veritatis illum, iste aut sit vero. Nesciunt tempore deserunt soluta eaque quibusdam, exercitationem ipsum nemo officiis minima molestiae cupiditate voluptatibus eius placeat sed repudiandae. Modi odit incidunt tenetur officia facere pariatur.",
      noOfReviews: 100,
    },
    {
      id: 6,
      title: "Product 6",
      price: 600000,
      image:
        "https://images.unsplash.com/photo-1633948393301-d43e3ec0e5cd?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      rating: 4.5,
      details:
        "Lorem ipsum dolor sit amet consectetur adipisicing elit. Dolores cumque, a voluptates porro sed atque voluptas voluptate repellendus ab veritatis illum, iste aut sit vero. Nesciunt tempore deserunt soluta eaque quibusdam, exercitationem ipsum nemo officiis minima molestiae cupiditate voluptatibus eius placeat sed repudiandae. Modi odit incidunt tenetur officia facere pariatur.",
      noOfReviews: 100,
    },
    {
      id: 7,
      title: "Product 4",
      price: 400000,
      image:
        "https://images.unsplash.com/photo-1633948393301-d43e3ec0e5cd?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      rating: 4.5,
      details:
        "Lorem ipsum dolor sit amet consectetur adipisicing elit. Dolores cumque, a voluptates porro sed atque voluptas voluptate repellendus ab veritatis illum, iste aut sit vero. Nesciunt tempore deserunt soluta eaque quibusdam, exercitationem ipsum nemo officiis minima molestiae cupiditate voluptatibus eius placeat sed repudiandae. Modi odit incidunt tenetur officia facere pariatur.",
      noOfReviews: 100,
    },
    {
      id: 8,
      title: "Product 5",
      price: 500000,
      image:
        "https://images.unsplash.com/photo-1633948393301-d43e3ec0e5cd?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      rating: 4.5,
      details:
        "Lorem ipsum dolor sit amet consectetur adipisicing elit. Dolores cumque, a voluptates porro sed atque voluptas voluptate repellendus ab veritatis illum, iste aut sit vero. Nesciunt tempore deserunt soluta eaque quibusdam, exercitationem ipsum nemo officiis minima molestiae cupiditate voluptatibus eius placeat sed repudiandae. Modi odit incidunt tenetur officia facere pariatur.",
      noOfReviews: 100,
    },
    {
      id: 9,
      title: "Product 6",
      price: 600000,
      image:
        "https://images.unsplash.com/photo-1633948393301-d43e3ec0e5cd?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      rating: 4.5,
      details:
        "Lorem ipsum dolor sit amet consectetur adipisicing elit. Dolores cumque, a voluptates porro sed atque voluptas voluptate repellendus ab veritatis illum, iste aut sit vero. Nesciunt tempore deserunt soluta eaque quibusdam, exercitationem ipsum nemo officiis minima molestiae cupiditate voluptatibus eius placeat sed repudiandae. Modi odit incidunt tenetur officia facere pariatur.",
      noOfReviews: 100,
    },
    {
      id: 1,
      title: "Product 1",
      price: 100000,
      image:
        "https://images.unsplash.com/photo-1633948393301-d43e3ec0e5cd?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      rating: 4.5,
      details:
        "Lorem ipsum dolor sit amet consectetur adipisicing elit. Dolores cumque, a voluptates porro sed atque voluptas voluptate repellendus ab veritatis illum, iste aut sit vero. Nesciunt tempore deserunt soluta eaque quibusdam, exercitationem ipsum nemo officiis minima molestiae cupiditate voluptatibus eius placeat sed repudiandae. Modi odit incidunt tenetur officia facere pariatur.",
      noOfReviews: 100,
      assetProtected: true,
    },
    {
      id: 2,
      title: "Product 2",
      price: 200000,
      image:
        "https://images.unsplash.com/photo-1633948393301-d43e3ec0e5cd?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      rating: 4.5,
      details:
        "Lorem ipsum dolor sit amet consectetur adipisicing elit. Dolores cumque, a voluptates porro sed atque voluptas voluptate repellendus ab veritatis illum, iste aut sit vero. Nesciunt tempore deserunt soluta eaque quibusdam, exercitationem ipsum nemo officiis minima molestiae cupiditate voluptatibus eius placeat sed repudiandae. Modi odit incidunt tenetur officia facere pariatur.",
      noOfReviews: 100,
      installationProtection: true,
    },
    {
      id: 3,
      title: "Product 3",
      price: 300000,
      image:
        "https://images.unsplash.com/photo-1633948393301-d43e3ec0e5cd?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      rating: 4.5,
      details:
        "Lorem ipsum dolor sit amet consectetur adipisicing elit. Dolores cumque, a voluptates porro sed atque voluptas voluptate repellendus ab veritatis illum, iste aut sit vero. Nesciunt tempore deserunt soluta eaque quibusdam, exercitationem ipsum nemo officiis minima molestiae cupiditate voluptatibus eius placeat sed repudiandae. Modi odit incidunt tenetur officia facere pariatur.",
      noOfReviews: 100,
    },
    {
      id: 4,
      title: "Product 4",
      price: 400000,
      image:
        "https://images.unsplash.com/photo-1633948393301-d43e3ec0e5cd?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      rating: 4.5,
      details:
        "Lorem ipsum dolor sit amet consectetur adipisicing elit. Dolores cumque, a voluptates porro sed atque voluptas voluptate repellendus ab veritatis illum, iste aut sit vero. Nesciunt tempore deserunt soluta eaque quibusdam, exercitationem ipsum nemo officiis minima molestiae cupiditate voluptatibus eius placeat sed repudiandae. Modi odit incidunt tenetur officia facere pariatur.",
      noOfReviews: 100,
      assetProtected: true,
      installationProtection: true,
    },
    {
      id: 5,
      title: "Product 5",
      price: 500000,
      image:
        "https://images.unsplash.com/photo-1633948393301-d43e3ec0e5cd?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      rating: 4.5,
      details:
        "Lorem ipsum dolor sit amet consectetur adipisicing elit. Dolores cumque, a voluptates porro sed atque voluptas voluptate repellendus ab veritatis illum, iste aut sit vero. Nesciunt tempore deserunt soluta eaque quibusdam, exercitationem ipsum nemo officiis minima molestiae cupiditate voluptatibus eius placeat sed repudiandae. Modi odit incidunt tenetur officia facere pariatur.",
      noOfReviews: 100,
    },
    {
      id: 6,
      title: "Product 6",
      price: 600000,
      image:
        "https://images.unsplash.com/photo-1633948393301-d43e3ec0e5cd?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      rating: 4.5,
      details:
        "Lorem ipsum dolor sit amet consectetur adipisicing elit. Dolores cumque, a voluptates porro sed atque voluptas voluptate repellendus ab veritatis illum, iste aut sit vero. Nesciunt tempore deserunt soluta eaque quibusdam, exercitationem ipsum nemo officiis minima molestiae cupiditate voluptatibus eius placeat sed repudiandae. Modi odit incidunt tenetur officia facere pariatur.",
      noOfReviews: 100,
    },
    {
      id: 7,
      title: "Product 4",
      price: 400000,
      image:
        "https://images.unsplash.com/photo-1633948393301-d43e3ec0e5cd?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      rating: 4.5,
      details:
        "Lorem ipsum dolor sit amet consectetur adipisicing elit. Dolores cumque, a voluptates porro sed atque voluptas voluptate repellendus ab veritatis illum, iste aut sit vero. Nesciunt tempore deserunt soluta eaque quibusdam, exercitationem ipsum nemo officiis minima molestiae cupiditate voluptatibus eius placeat sed repudiandae. Modi odit incidunt tenetur officia facere pariatur.",
      noOfReviews: 100,
    },
    {
      id: 8,
      title: "Product 5",
      price: 500000,
      image:
        "https://images.unsplash.com/photo-1633948393301-d43e3ec0e5cd?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      rating: 4.5,
      details:
        "Lorem ipsum dolor sit amet consectetur adipisicing elit. Dolores cumque, a voluptates porro sed atque voluptas voluptate repellendus ab veritatis illum, iste aut sit vero. Nesciunt tempore deserunt soluta eaque quibusdam, exercitationem ipsum nemo officiis minima molestiae cupiditate voluptatibus eius placeat sed repudiandae. Modi odit incidunt tenetur officia facere pariatur.",
      noOfReviews: 100,
    },
    {
      id: 9,
      title: "Product 6",
      price: 600000,
      image:
        "https://images.unsplash.com/photo-1633948393301-d43e3ec0e5cd?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      rating: 4.5,
      details:
        "Lorem ipsum dolor sit amet consectetur adipisicing elit. Dolores cumque, a voluptates porro sed atque voluptas voluptate repellendus ab veritatis illum, iste aut sit vero. Nesciunt tempore deserunt soluta eaque quibusdam, exercitationem ipsum nemo officiis minima molestiae cupiditate voluptatibus eius placeat sed repudiandae. Modi odit incidunt tenetur officia facere pariatur.",
      noOfReviews: 100,
    },
  ];

  const DEFAULT_ITEMS: JumbotronItem[] = [
    {
      id: 1,
      productLink: "/products/1",
      image:
        "https://images.unsplash.com/photo-1764757146502-a8c0990a8479?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    },
    {
      id: 2,
      productLink: "/products/2",
      image:
        "https://images.unsplash.com/photo-1764757146502-a8c0990a8479?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    },
    {
      id: 3,
      productLink: "/products/3",
      image:
        "https://images.unsplash.com/photo-1764757146502-a8c0990a8479?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    },
    {
      id: 4,
      productLink: "/products/4",
      image:
        "https://images.unsplash.com/photo-1764757146502-a8c0990a8479?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    },
    {
      id: 5,
      productLink: "/products/5",
      image:
        "https://images.unsplash.com/photo-1764757146502-a8c0990a8479?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    },
  ];

  return (
    <div className='flex min-h-svh w-full flex-col'>
      <div className='relative h-[40vh] w-full bg-linear-to-t from-[#2FB3FF] to-[#155DFC] md:h-[60vh] md:min-h-[430px]'>
        <ShopPageJumbotron items={DEFAULT_ITEMS} autoplay={true} autoplayDelay={3000} pauseOnHover={true} loop={true} />
      </div>
      <div className='flex w-full gap-4 px-0 xl:mt-12 xl:px-5 xl:py-6'>
        <aside className='hidden w-1/5 shrink-0 xl:block'>
          <ProductFilters
            categories={categories}
            brands={brands}
            checkedCategories={checkedCategories}
            checkedBrands={checkedBrands}
            onCategoryChange={(categoryId, checked) => {
              setCheckedCategories((prev) => ({
                ...prev,
                [categoryId]: checked,
              }));
            }}
            onBrandChange={(brandId, checked) => {
              setCheckedBrands((prev) => ({
                ...prev,
                [brandId]: checked,
              }));
            }}
          />
        </aside>
        <main className='flex-1'>
          <div className='mb-2 px-3 py-3 xl:hidden'>
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant='outline' className='h-10 rounded-lg border-[1.4px] px-3'>
                  <AdjustmentsHorizontalIcon className='text-icon-title size-4 stroke-[1.5px]' />
                  <span className='text-text-title text-sm'>Filters</span>
                </Button>
              </SheetTrigger>
              <SheetContent side='left' className='py-6'>
                <div className='flex w-full items-center justify-between px-3'>
                  <SheetClose asChild>
                    <XMarkIcon className='text-icon-title size-5 stroke-2' />
                  </SheetClose>
                  <Button onClick={() => setIsOpen(false)}>Apply Filter</Button>
                </div>
                <div className='overflow-y-auto px-3'>
                  <ProductFilters
                    categories={categories}
                    brands={brands}
                    checkedCategories={checkedCategories}
                    checkedBrands={checkedBrands}
                    onCategoryChange={(categoryId, checked) => {
                      setCheckedCategories((prev) => ({
                        ...prev,
                        [categoryId]: checked,
                      }));
                    }}
                    onBrandChange={(brandId, checked) => {
                      setCheckedBrands((prev) => ({
                        ...prev,
                        [brandId]: checked,
                      }));
                    }}
                  />
                </div>
              </SheetContent>
            </Sheet>
          </div>
          <div className='border-border-primmary/30 relative grid grid-cols-2 gap-4 border-y bg-[#F9FAFB] px-3 py-6 md:grid-cols-3 md:gap-8 md:px-8 xl:border-0 xl:bg-transparent xl:py-0 dark:bg-transparent'>
            <Button
              className='absolute top-0 right-0 z-20'
              onClick={() => {
                setIsLoading(!isLoading);
              }}
            >
              Toggle Loading
            </Button>
            {isLoading
              ? [1, 2, 3, 4, 5, 6, 7, 8, 9].map((item) => <ProductCardSkeletonLoader size='md' key={item} />)
              : products.map((product) => <ProductCard size='md' key={product.id} {...product} />)}
          </div>
          {!isLoading && <PaginationControls meta={meta} className='mt-12' />}
        </main>
      </div>

      <CTASection />
      <Footer />
    </div>
  );
}

export default function ShopPage() {
  // todo: need a suspense loader
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <ShopContent />
    </Suspense>
  );
}
